/**
 * Single-roll loss distributions for Risk combat.
 *
 * Both sides sort their dice descending and compare pairwise for `min(na, nd)`
 * pairs. The attacker wins a pair only on a strict `>` — **ties go to the
 * defender**. Every pair costs exactly one army to one side, so for a given
 * roll `attackerLosses + defenderLosses === comparisons`.
 *
 * Distributions are stored as exact integer numerators over `6 ** (na + nd)`.
 * Nothing here uses floating point, so the table is exact rather than
 * "exact to within a rounding error someone transcribed from a web page".
 */

export interface LossOutcome {
  readonly attackerLosses: number;
  readonly defenderLosses: number;
  readonly numerator: number;
}

export interface LossDistribution {
  readonly attackerDice: number;
  readonly defenderDice: number;
  readonly denominator: number;
  /** Indexed by `attackerLosses`, length `comparisons + 1`. */
  readonly outcomes: readonly LossOutcome[];
}

/** Guard against an accidental 6**n blow-up from a bad rules config. */
const MAX_TOTAL_DICE = 10;

export interface BattleLosses {
  readonly attackerLosses: number;
  readonly defenderLosses: number;
}

/**
 * Resolve one roll: sort both sides descending, compare pairwise, ties to the
 * defender.
 *
 * The enumerator below is built on this same function, so the published odds
 * and a live battle can never disagree about who won a pair.
 */
export function resolveDiceRoll(
  attacker: readonly number[],
  defender: readonly number[],
): BattleLosses {
  const a = [...attacker].sort((x, y) => y - x);
  const d = [...defender].sort((x, y) => y - x);
  const comparisons = Math.min(a.length, d.length);

  let attackerLosses = 0;
  for (let i = 0; i < comparisons; i += 1) {
    if (a[i]! <= d[i]!) attackerLosses += 1;
  }
  return { attackerLosses, defenderLosses: comparisons - attackerLosses };
}

/**
 * Brute-force every possible roll. This is the oracle: the shipped
 * `DICE_TABLE` constants are asserted against this function in the test
 * suite, so a typo in the table can never reach a player.
 */
export function enumerateLossDistribution(
  attackerDice: number,
  defenderDice: number,
): LossDistribution {
  if (!Number.isInteger(attackerDice) || attackerDice < 1) {
    throw new RangeError(`attackerDice must be a positive integer, got ${attackerDice}`);
  }
  if (!Number.isInteger(defenderDice) || defenderDice < 1) {
    throw new RangeError(`defenderDice must be a positive integer, got ${defenderDice}`);
  }
  const total = attackerDice + defenderDice;
  if (total > MAX_TOTAL_DICE) {
    throw new RangeError(`refusing to enumerate 6**${total} outcomes`);
  }

  const comparisons = Math.min(attackerDice, defenderDice);
  const counts = new Array<number>(comparisons + 1).fill(0);
  const dice = new Array<number>(total).fill(1);

  for (;;) {
    const { attackerLosses } = resolveDiceRoll(
      dice.slice(0, attackerDice),
      dice.slice(attackerDice),
    );
    counts[attackerLosses]! += 1;

    // Odometer over all 6**total rolls.
    let i = total - 1;
    while (i >= 0 && dice[i] === 6) {
      dice[i] = 1;
      i -= 1;
    }
    if (i < 0) break;
    dice[i]! += 1;
  }

  return {
    attackerDice,
    defenderDice,
    denominator: 6 ** total,
    outcomes: counts.map((numerator, attackerLosses) => ({
      attackerLosses,
      defenderLosses: comparisons - attackerLosses,
      numerator,
    })),
  };
}

/**
 * The six pairings reachable under standard rules (attacker rolls up to 3,
 * defender up to 2). Verified against `enumerateLossDistribution` in the tests.
 */
export const DICE_TABLE: Readonly<Record<string, LossDistribution>> = Object.freeze({
  '1x1': dist(1, 1, 36, [15, 21]),
  '2x1': dist(2, 1, 216, [125, 91]),
  '3x1': dist(3, 1, 1296, [855, 441]),
  '1x2': dist(1, 2, 216, [55, 161]),
  '2x2': dist(2, 2, 1296, [295, 420, 581]),
  '3x2': dist(3, 2, 7776, [2890, 2611, 2275]),
});

/** `numerators` is indexed by attackerLosses. */
function dist(
  attackerDice: number,
  defenderDice: number,
  denominator: number,
  numerators: readonly number[],
): LossDistribution {
  const comparisons = Math.min(attackerDice, defenderDice);
  return {
    attackerDice,
    defenderDice,
    denominator,
    outcomes: numerators.map((numerator, attackerLosses) => ({
      attackerLosses,
      defenderLosses: comparisons - attackerLosses,
      numerator,
    })),
  };
}

const enumerated = new Map<string, LossDistribution>();

/**
 * Loss distribution for a pairing: the published table when it covers the
 * pairing, otherwise enumerated on demand and cached (house rules can raise
 * the dice caps).
 */
export function lossDistribution(attackerDice: number, defenderDice: number): LossDistribution {
  const key = `${attackerDice}x${defenderDice}`;
  const known = DICE_TABLE[key];
  if (known) return known;

  let computed = enumerated.get(key);
  if (!computed) {
    computed = enumerateLossDistribution(attackerDice, defenderDice);
    enumerated.set(key, computed);
  }
  return computed;
}
